import { get, writable } from 'svelte/store';
import * as ramda from 'ramda';
import type { Writable } from 'svelte/store';

import ServiceError from '$shared/models/ServiceError';
import type { AlertDetailedInfo } from '$shared/models/AlertDetailedInfo';

export type BaseStoreType = {
  isLoading: boolean;
  error: AlertDetailedInfo | string | undefined;
};

export const BaseStoreInitialState: BaseStoreType = {
  isLoading: false,
  error: ''
};

export default class BaseStore<T> {
  protected store: Writable<T>;

  private InitialState: T;

  constructor(initialState: T) {
    this.InitialState = initialState;
    this.store = writable<T>(ramda.clone({ ...this.InitialState }));
  }

  public get subscribe() {
    return this.store.subscribe;
  }

  public get error(): string | AlertDetailedInfo | undefined {
    return this.getValue<string>('error');
  }

  public get isLoading(): boolean {
    return this.getValue('isLoading');
  }

  public setLoading(isLoading: boolean): void {
    this.update('isLoading', isLoading);
  }

  public setError(error: ServiceError | string | unknown): void {
    let errorData: any = error;
    if (error instanceof ServiceError) {
      errorData = error?.errorAlert || error?.message || error?.title;
    }
    this.update('error', errorData);
  }

  public resetError(): void {
    this.update('error', undefined);
  }

  public reset(): void {
    this.store.set(ramda.clone({ ...this.InitialState }));
  }

  protected getValue<R>(key: string): R {
    const store = get<T>(this.store);
    return store[key as keyof T] as R;
  }

  protected update(key: string, value: any): void {
    this.store.update((state: T) => {
      state[key as keyof T] = value;
      return state;
    });
  }
}
